import { useMatchAnyRoute } from "@circle-react/hooks/useMatchAnyRoute";
import { ABOUT_CIRCLE_V4_PATH } from "react/components/NewVersionOptIn/constants";
import { useOptInBarDismiss } from "react/components/NewVersionOptIn/OptInBar/useOptInBarDismiss";
import { usePunditUserContext } from "react/contexts/punditUserContext";
import { isCommunityAdmin } from "react/helpers/communityMemberHelpers";
import { useCircleV4 } from "react/hooks/useCircleV4";

const OPT_IN_BAR_DISABLED_ROUTES = [
  ABOUT_CIRCLE_V4_PATH,
  "/live/$roomSlug/not_supported",
  "/live/$roomSlug/record",
  "/live/$roomSlug/mobile",
  "/live/$roomSlug/public",
  "/live/$roomSlug",
  "/two_fa",
  "/discover",
] as const;

interface UseCircleV4OptInOptions {
  ignoreRouteRestrictions?: boolean;
}

export const useCircleV4OptIn = ({
  ignoreRouteRestrictions = false,
}: UseCircleV4OptInOptions = {}) => {
  const { currentCommunityMember } = usePunditUserContext();
  const {
    isCircleV4Eligible,
    isCircleV4OptInEnabled,
    isCircleV4PreviewEnabled,
    isCircleV4Enabled,
  } = useCircleV4();
  const matchAnyRoute = useMatchAnyRoute();
  const isOptInBarDisabledOnRoute = Boolean(
    matchAnyRoute(OPT_IN_BAR_DISABLED_ROUTES),
  );
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const {
    dismiss: dismissOptInBar,
    shouldShow: shouldShowOptInBarAfterDismiss,
  } = useOptInBarDismiss();

  // All three bars (opt-in, waitlist, preview) render everywhere, including
  // v3/v4 settings; only full-screen routes (live, 2FA) are excluded.
  const shouldDisplayBarOnCurrentRoute =
    ignoreRouteRestrictions || !isOptInBarDisabledOnRoute;

  const shouldShowWaitlistBar =
    shouldDisplayBarOnCurrentRoute &&
    isCircleV4Eligible &&
    !isCircleV4OptInEnabled &&
    !isCircleV4PreviewEnabled &&
    !isCircleV4Enabled &&
    isAdmin &&
    shouldShowOptInBarAfterDismiss;

  const shouldShowOptInBar =
    shouldDisplayBarOnCurrentRoute &&
    isCircleV4Eligible &&
    isCircleV4OptInEnabled &&
    !isCircleV4PreviewEnabled &&
    !isCircleV4Enabled &&
    isAdmin;

  const shouldShowPreviewEnabledBar =
    shouldDisplayBarOnCurrentRoute &&
    isCircleV4PreviewEnabled &&
    !isCircleV4Enabled &&
    isAdmin;

  return {
    dismissOptInBar,
    shouldShowOptInBar,
    shouldShowPreviewEnabledBar,
    shouldShowWaitlistBar,
  };
};
